import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { t, type IntlService } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { PhShieldCheck, PhCurrencyDollar } from 'ember-phosphor-icons';
import { Chip } from 'tio-ui/components/buttons';
import { VStack } from 'tio-ui/components/layout';
import type CompanySettingModel from '../../models/company-setting';
import type CompanyLpnSetting from '../../models/company-lpn-setting';
import type LpnPage from '../../models/lpn-page';

interface LpnCardSignature {
  Args: {
    lpn: LpnPage;
    companySettings: CompanySettingModel;
  };
  Element: HTMLElement;
  Blocks: Record<string, never>;
}

export default class LpnCard extends Component<LpnCardSignature> {
  @service declare intl: IntlService;

  companyLpnSettingState = trackedFunction(this, async () => {
    return await this.args.lpn.settingForCompany(this.args.companySettings);
  });

  get companyLpnSetting() {
    return this.companyLpnSettingState.value as CompanyLpnSetting | undefined;
  }

  get mergedLpn() {
    const overrides = (this.companyLpnSetting ?? {}) as Record<string, unknown>;
    return new Proxy(this.args.lpn as LpnPage, {
      get(target, prop: string | symbol) {
        if (typeof prop === 'string') {
          const value = overrides[prop];
          // Only use the company-lpn-setting value if it's meaningfully set.
          // Column defaults (false, 0, '', []) should NOT override the lpn-page.
          if (
            value !== null &&
            value !== undefined &&
            value !== false &&
            value !== '' &&
            !(Array.isArray(value) && value.length === 0)
          ) {
            return value;
          }
          return (target as unknown as Record<string, unknown>)[prop];
        }
        return (target as unknown as Record<symbol, unknown>)[prop];
      },
    }) as LpnPage;
  }

  get hasPrimaryDiscount() {
    if (this.mergedLpn.hasDiscount === true) {
      return true;
    }
    return Number(this.mergedLpn.discountPercent) > 0;
  }

  get providedBy() {
    return this.mergedLpn.providedBy?.trim();
  }

  get firstParagraph() {
    // Use companyLpnSetting description if it has blocks, otherwise fall back to lpnPage description
    const companyBlocks = this.companyLpnSetting?.description?.blocks ?? [];
    const lpnBlocks = this.args.lpn.description?.blocks ?? [];
    const blocks = companyBlocks.length > 0 ? companyBlocks : lpnBlocks;

    const paragraph = blocks.find((block) => block.type === 'paragraph');

    if (!paragraph) {
      return htmlSafe(blocks[0]?.data?.text ?? '');
    }

    return htmlSafe(paragraph?.data?.text ?? '');
  }

  get cappedTuitionBadge() {
    if (!this.mergedLpn.badgeCappedTuition) {
      return null;
    }
    const range = (this.mergedLpn.badgeCappedTuitionRange ?? '').trim();
    if (range.length > 0) {
      return `${this.intl.t('lpn.badges.capped_tuition_at')} $${range}`;
    }
    return this.intl.t('lpn.badges.capped_tuition');
  }

  get savingsPercentBadge() {
    if (this.mergedLpn.useDiscountPercentRange) {
      return `${this.intl.t('lpn.badges.save')} ${this.mergedLpn.discountPercentRange}%`;
    }
    if (this.mergedLpn.badgeDiscount) {
      return `${this.intl.t('lpn.badges.save')} ${this.mergedLpn.discountPercent}%`;
    }
    return null;
  }

  get savingsAmountBadge() {
    if (this.mergedLpn.useSaveDollarAmountRange) {
      return `${this.intl.t('lpn.badges.save')} $${this.mergedLpn.saveDollarAmountRange}`;
    }
    if (this.mergedLpn.saveDollarAmount) {
      return `${this.intl.t('lpn.badges.save')} $${this.mergedLpn.saveDollarAmountValue}`;
    }
    return null;
  }

  get costPerCreditBadge() {
    if (this.mergedLpn.useCostPerCreditRange) {
      return `${this.intl.t('lpn.badges.cost_per_credit')}: $${this.mergedLpn.costPerCreditRange}`;
    }
    if (this.mergedLpn.costPerCredit && this.mergedLpn.costPerCredit.length > 0) {
      return `${this.intl.t('lpn.badges.cost_per_credit')}: $${this.mergedLpn.costPerCredit}`;
    }
    return null;
  }
  get tags() {
    return [
      // Employer pick
      this.mergedLpn.employerRecommended ? this.intl.t('lpn.employer_recommended') : null,
      // Capped Tuition
      this.cappedTuitionBadge,
      // Savings Percent
      this.savingsPercentBadge,
      // Savings Amount
      this.savingsAmountBadge,
      // Part Tuition Waived
      this.mergedLpn.partTuitionWaiver ? this.intl.t('lpn.badges.part_tuition_waiver') : null,
      // Low Tuition
      this.mergedLpn.lowTuition ? this.intl.t('lpn.badges.low_tuition') : null,
      // Family Eligible
      this.mergedLpn.badgeFamilySavings ? this.intl.t('lpn.badges.family_eligible') : null,
      // No Fees
      this.mergedLpn.badgeFeesWaived ? this.intl.t('lpn.badges.no_fees') : null,
      // Cost Per Credit
      this.costPerCreditBadge,
      // Formats
      ...(this.mergedLpn.format ?? []),
      // Programs
      ...(this.mergedLpn.programs ?? []),
    ]
      .filter(Boolean)
      .slice(0, 12);
  }
  <template>
    <VStack @collapsed={{true}} class="border border-gray-200 rounded-2xl bg-white p-4 shadow-sm">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-3">
          {{#if this.mergedLpn.imageUrl}}
            {{! <div class="flex h-12 w-40 items-center justify-center rounded border border-gray-100 bg-white"> }}
            <img
              src="{{this.mergedLpn.imageUrl}}"
              alt="{{this.mergedLpn.schoolName}}"
              width="275"
              height="90"
            />
            {{! </div> }}
          {{/if}}
        </div>
        {{! <button
          type="button"
          class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-400"
          aria-label="Favorite"
        >
          <PhStar @size="18" />
        </button> }}
      </div>

      <div class="flex min-h-[10px] flex-wrap gap-2 text-sm text-gray-700">
        {{#if this.mergedLpn.employerRecommended}}
          <div class="flex items-center gap-1">
            <PhShieldCheck @size="16" class="text-gray-500" />
            <span>{{t "lpn.employer_recommended"}}</span>
          </div>
        {{/if}}
        {{#if this.hasPrimaryDiscount}}
          <div class="flex items-center gap-1">
            <PhCurrencyDollar @size="16" class="text-gray-500" />
            <span>{{t "lpn.badges.primary_discount"}}</span>
          </div>
        {{/if}}
      </div>

      <h3 class="mt-1 text-xl font-semibold text-gray-900">{{this.mergedLpn.schoolName}}</h3>

      <div class="min-h-[14px]">
        {{#if this.providedBy}}
          <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {{t "lpn.provided_by"}}
            <span class="text-gray-700">{{this.providedBy}}</span>
          </p>
        {{/if}}
      </div>

      <p class="mt-1 min-h-[84px] text-sm text-gray-600">
        {{this.firstParagraph}}
      </p>

      <div class="mt-3 min-h-[20px]">
        {{#if this.tags.length}}
          <div class="flex flex-wrap gap-2">
            {{#each this.tags as |tag|}}
              <Chip @appearance="outlined" class="bg-gray-50 text-gray-700">
                <span class="text-xs whitespace-nowrap">{{tag}}</span>
              </Chip>
            {{/each}}
          </div>
        {{/if}}
      </div>
    </VStack>
  </template>
}
